/**
 * React Query hooks for the Inference Admin screen. Polling cadences:
 *   - Usage (overview tab): 120s — aggregates change slowly
 *   - Jobs: 60s — new rows trickle in continuously
 *   - Customers list + detail: no polling; refetched on mutation
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateCustomerInput,
  createInferenceApiKey,
  createInferenceCustomer,
  fetchInferenceCustomer,
  fetchInferenceCustomers,
  fetchInferenceJobs,
  fetchInferenceUsage,
  revokeInferenceApiKey
} from '@/api/inferenceAdmin';

export function useInferenceUsage(days = 7) {
  return useQuery({
    queryKey: ['inferenceUsage', days],
    queryFn: () => fetchInferenceUsage(days),
    refetchInterval: 120_000
  });
}

export function useInferenceCustomers(params: {
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['inferenceCustomers', params],
    queryFn: () => fetchInferenceCustomers(params),
    placeholderData: prev => prev
  });
}

export function useInferenceCustomer(id: string | null) {
  return useQuery({
    queryKey: ['inferenceCustomer', id],
    queryFn: () => fetchInferenceCustomer(id!),
    enabled: Boolean(id)
  });
}

export function useCreateInferenceCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerInput) => createInferenceCustomer(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inferenceCustomers'] });
    }
  });
}

export function useCreateInferenceApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { customerId: string; name?: string }) =>
      createInferenceApiKey(input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['inferenceCustomer', vars.customerId] });
    }
  });
}

export function useRevokeInferenceApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { customerId: string; keyId: string }) =>
      revokeInferenceApiKey(input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['inferenceCustomer', vars.customerId] });
    }
  });
}

export function useInferenceJobs(params: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['inferenceJobs', params],
    queryFn: () => fetchInferenceJobs(params),
    refetchInterval: 60_000,
    placeholderData: prev => prev
  });
}
